import React, { useState } from 'react';
import { getApiErrorMessages } from "../utils/apiError";
import { Link, useNavigate } from 'react-router-dom';
import { safeNavigate } from '../utils/navigationUtils';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Lock, Eye, EyeOff, UserPlus, Check, X, Phone } from 'lucide-react';
import logo from '../assets/logo6.png';
import { GoogleLogin } from '@react-oauth/google';
import { t as tr } from '../contexts/translations';

const Register = ({ language }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const activeLanguage = language || localStorage.getItem('language') || 'fr';
  const T = (key, fallback) => {
    const v = tr(key, activeLanguage);
    return v === key ? fallback : v;
  };

  // Password validation
  const passwordRequirements = {
    length: password.length >= 6,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password)
  };

  const isPasswordValid = Object.values(passwordRequirements).every(req => req);
  const passwordsMatch = password === confirmPassword && confirmPassword !== '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation des mots de passe
    if (password !== confirmPassword) {
      return setError(T('passwordMismatch', 'Les mots de passe ne correspondent pas'));
    }
    
    // Validation de la complexité du mot de passe
    if (!isPasswordValid) {
      return setError(T('passwordComplexity', 'Le mot de passe doit contenir au moins 6 caractères, dont une majuscule, une minuscule et un chiffre'));
    }

    // Validation du numéro de téléphone
    if (!phone) {
      return setError(T('phoneRequired', 'Le numéro de téléphone est requis'));
    }
    
    try {
      setError('');
      setLoading(true);
      
      await register(username, email, password, phone);
      
      // Redirection vers la page de connexion après inscription réussie
      safeNavigate(navigate, '/login', { state: { message: T('registerSuccess', 'Inscription réussie. Vous pouvez maintenant vous connecter.') } });
    } catch (err) {
      console.error('Erreur d\'inscription:', err);
      setError(T('registerErrorPrefix', 'Échec de l\'inscription. ') + getApiErrorMessages(err, T('pleaseRetry', 'Veuillez réessayer.')).join(' • '));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setError('');
      setLoading(true);

      const idToken = credentialResponse.credential; // id_token Google
      await googleLogin(idToken);

      // Redirection après connexion Google
      safeNavigate(navigate, '/', {
        state: { message: T('googleLoginSuccess', 'Connexion via Google réussie.') }
      });
    } catch (err) {
      console.error('Erreur Google Login :', err);
      setError(T('googleLoginError', "Échec de la connexion avec Google. Veuillez réessayer."));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError(T('googleLoginFailed', "La connexion avec Google a échoué."));
  };


  const RequirementItem = ({ met, text }) => (
    <div className={`flex items-center gap-2 text-xs transition-colors duration-300 ${
      met ? 'text-green-600' : 'text-gray-500'
    }`}>
      {met ? (
        <Check className="w-3 h-3" />
      ) : (
        <X className="w-3 h-3" />
      )}
      <span>{text}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <Link to="/" className="flex items-center group">
              <img 
                src={logo} 
                alt="PalaisTouristiques" 
                className="h-20 w-auto transition-all duration-500"
              />
            </Link>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {T('registerWelcomeTitle', 'Rejoignez-nous !')}
          </h2>
          <p className="text-gray-600">
            {T('registerWelcomeSubtitle', "Créez votre compte et découvrez l'authenticité du Maroc")}
          </p>
        </div>
        
        {/* Main Form Card */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-orange-200/50 p-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="mb-6">
            <div className="w-full flex justify-center">
              <div className="w-full max-w-sm flex justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                   width={520}
                  size="large"
                  theme="outline"
                  shape="pill"
                  text="continue_with"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="px-3 text-xs text-gray-500 uppercase">{T('or', 'ou')}</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
          </div>

          
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                {T('username', "Nom d'utilisateur")}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 bg-white/50 backdrop-blur-sm"
                  placeholder={T('usernamePlaceholder', "Votre nom d'utilisateur")}
                />
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-2">
                {T('emailLabel', 'Adresse email')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 bg-white/50 backdrop-blur-sm"
                  placeholder="votre@email.com"
                />
              </div>
            </div>

            {/* Phone Field */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                {T('phoneLabel', 'Numéro de téléphone')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 bg-white/50 backdrop-blur-sm"
                  placeholder="+212 6XX XXXXXX"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {T('password', 'Mot de passe')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300 bg-white/50 backdrop-blur-sm"
                  placeholder={T('passwordPlaceholder', 'Votre mot de passe')}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              
              {/* Password Requirements */}
              {password && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-1">
                  <RequirementItem met={passwordRequirements.length} text={T('pwdRuleLength', 'Au moins 6 caractères')} />
                  <RequirementItem met={passwordRequirements.uppercase} text={T('pwdRuleUpper', 'Une lettre majuscule')} />
                  <RequirementItem met={passwordRequirements.lowercase} text={T('pwdRuleLower', 'Une lettre minuscule')} />
                  <RequirementItem met={passwordRequirements.number} text={T('pwdRuleNumber', 'Un chiffre')} />
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                {T('confirmPassword', 'Confirmer le mot de passe')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirm-password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`block w-full pl-10 pr-10 py-3 border rounded-xl placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 transition-all duration-300 bg-white/50 backdrop-blur-sm ${
                    confirmPassword && passwordsMatch
                      ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                      : confirmPassword && !passwordsMatch
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-orange-500 focus:border-orange-500'
                  }`}
                  placeholder={T('confirmPasswordPlaceholder', 'Confirmez votre mot de passe')}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              
              {/* Password Match Indicator */}
              {confirmPassword && (
                <div className={`mt-2 text-xs flex items-center gap-2 ${
                  passwordsMatch ? 'text-green-600' : 'text-red-600'
                }`}>
                  {passwordsMatch ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <X className="w-3 h-3" />
                  )}
                  <span>
                    {passwordsMatch ? T('passwordsMatch', 'Les mots de passe correspondent') : T('passwordMismatch', 'Les mots de passe ne correspondent pas')}
                  </span>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !isPasswordValid || !passwordsMatch}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-[1.02] shadow-lg"
            >
              <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                <UserPlus className="h-5 w-5 text-orange-300 group-hover:text-orange-200" />
              </span>
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  {T('registering', 'Inscription en cours...')}
                </div>
              ) : (
                T('createMyAccount', 'Créer mon compte')
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {T('alreadyRegisteredPrompt', 'Déjà inscrit ?')} {' '}
              <Link 
                to="/login" 
                className="font-medium text-orange-600 hover:text-orange-500 transition-colors duration-300"
              >
                {T('loginButton', 'Se connecter')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;